import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist";

/** PDF.js scale (1 = 100% at 72 dpi). Slightly above 100% for biller/preview readability. */
const PREVIEW_ZOOM = 1.25;

let pdfJsWorkerConfigured = false;

function ensurePdfJsWorker(): void {
  if (pdfJsWorkerConfigured) {
    return;
  }
  pdfjsLib.GlobalWorkerOptions.workerSrc = "assets/pdfjs/pdf.worker.min.js";
  pdfJsWorkerConfigured = true;
}

@Component({
  selector: "app-pdf-preview",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./pdf-preview.component.html",
  styleUrls: ["./pdf-preview.component.scss"],
})
export class PdfPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() pdfFile: Blob | ArrayBuffer | Uint8Array | null = null;

  @ViewChild("viewerContainer") viewerContainer?: ElementRef<HTMLDivElement>;
  @ViewChild("pagesContainer") pagesContainer?: ElementRef<HTMLDivElement>;

  totalPages = 0;
  loading = false;
  errorMessage = "";

  private pdfDocument: PDFDocumentProxy | null = null;
  private renderTasks: RenderTask[] = [];
  private loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
  private viewInitialized = false;
  private loadGeneration = 0;
  private renderGeneration = 0;
  private destroyed = false;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    ensurePdfJsWorker();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    queueMicrotask(() => void this.loadPdf());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["pdfFile"] && this.viewInitialized) {
      void this.loadPdf();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadGeneration++;
    this.renderGeneration++;
    this.cancelRender();
    void this.loadingTask?.destroy();
    this.loadingTask = null;
    void this.pdfDocument?.destroy();
    this.pdfDocument = null;
  }

  async loadPdf(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.renderGeneration++;
    this.cancelRender();

    void this.loadingTask?.destroy();
    this.loadingTask = null;
    void this.pdfDocument?.destroy();
    this.pdfDocument = null;

    this.totalPages = 0;
    this.errorMessage = "";

    if (!this.pdfFile) {
      this.loading = false;
      return;
    }

    this.loading = true;

    try {
      const data = await this.toPdfData(this.pdfFile);
      if (generation !== this.loadGeneration || this.destroyed) {
        return;
      }

      const task = pdfjsLib.getDocument({ data });
      this.loadingTask = task;
      const document = await task.promise;

      if (generation !== this.loadGeneration || this.destroyed) {
        void document.destroy();
        return;
      }

      this.pdfDocument = document;
      this.totalPages = document.numPages;
    } catch (error) {
      if (generation !== this.loadGeneration || this.destroyed) {
        return;
      }
      this.errorMessage = this.getErrorMessage(error);
    } finally {
      if (generation === this.loadGeneration && !this.destroyed) {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      }
    }

    if (
      generation === this.loadGeneration &&
      !this.destroyed &&
      this.pdfDocument &&
      !this.errorMessage
    ) {
      await this.renderDocumentAfterViewReady();
    }
  }

  private async renderDocumentAfterViewReady(): Promise<void> {
    for (let attempt = 0; attempt < 8; attempt++) {
      this.changeDetectorRef.markForCheck();
      if (this.pagesContainer && this.viewerContainer) {
        await this.renderDocument();
        return;
      }
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
  }

  private async renderDocument(): Promise<void> {
    if (!this.pdfDocument || !this.pagesContainer || !this.viewerContainer) {
      return;
    }

    const generation = ++this.renderGeneration;
    this.cancelRender();

    const container = this.pagesContainer.nativeElement;
    container.innerHTML = "";

    try {
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        if (generation !== this.renderGeneration || this.destroyed) {
          return;
        }

        const page: PDFPageProxy = await this.pdfDocument.getPage(pageNum);
        if (generation !== this.renderGeneration || this.destroyed) {
          return;
        }

        const shell = document.createElement("div");
        shell.className = "page-shell";
        const canvas = document.createElement("canvas");
        canvas.className = "pdf-canvas";
        shell.appendChild(canvas);
        container.appendChild(shell);

        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Unable to create a canvas rendering context.");
        }

        const viewport = page.getViewport({ scale: PREVIEW_ZOOM });
        const outputScale = Math.min(window.devicePixelRatio || 1, 2);

        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const transform: [number, number, number, number, number, number] | undefined =
          outputScale !== 1
            ? [outputScale, 0, 0, outputScale, 0, 0]
            : undefined;

        const task = page.render({
          canvasContext: context,
          viewport,
          transform,
        });

        this.renderTasks.push(task);
        await task.promise;
      }
    } catch (error) {
      if (generation !== this.renderGeneration || this.destroyed) {
        return;
      }
      if (!(error instanceof Error && error.name === "RenderingCancelledException")) {
        this.errorMessage = this.getErrorMessage(error);
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  private cancelRender(): void {
    for (const task of this.renderTasks) {
      try {
        task.cancel();
      } catch {
        // The render may already have completed.
      }
    }
    this.renderTasks = [];
  }

  private async toPdfData(
    file: Blob | ArrayBuffer | Uint8Array
  ): Promise<Uint8Array> {
    if (file instanceof Blob) {
      return new Uint8Array(await file.arrayBuffer());
    }

    if (file instanceof ArrayBuffer) {
      return new Uint8Array(file.slice(0));
    }

    return new Uint8Array(file);
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return "Unable to load the PDF. Please check the file and try again.";
  }
}
